import React from 'react';
import { H1, PSmall } from '../../components/elements/Text';
import Navbar from '../../components/Navbar';
import PageWithAsyncData from '../../components/PageWithAsyncData';
import styled, { useTheme, css } from 'styled-components';
import {
  useGetAssessmentTests,
  useGetCompany,
} from '../../hooks/use-hydra/admin';

import { useHistory, useParams } from 'react-router-dom';
import { COMPANY_PAGES } from '../../components/Admin/CompanySideNavigation';
import CompanyPageWrapper from '../../components/Admin/CompanyPageWrapper';
import dayjs from 'dayjs';
import Table from '../../components/Table/CompactTable';
import { createColumnHelper } from '@tanstack/react-table';
import { SmallPortalTooltip } from '../../components/Tooltip';
import {
  TooltipDetailHighlight,
  TooltipContent,
  TooltipDetail,
  TooltipTitle,
} from '../../components/Admin/Progress/Tooltip';
import CollapsibleSection, {
  CollapsibleSectionFooter,
  CollapsibleSectionHeader,
  CollapsibleSectionLabel,
} from '../../components/Admin/CollapsibleSection';
import { MinimalAssessmentTestResult, TestAssignment } from '../../api/admin';
import { TopicPill, TopicText } from '../../components/Admin/TopicPill';
import { ROUTES } from '../../routing';
import StatusCell from '../../components/Admin/AssessmenTest/TestStatusCell';
import DateCellContent from '../../components/Admin/AssessmenTest/DateCellContent';
import CohortStats from '../../components/Admin/AssessmenTest/CohortStats';
import {
  TextFilterPill,
  MultiSelectFilterPill,
  RangeFilterPill,
  FilterBar,
  Toggle,
} from '../../components/Admin/AssessmenTest/Filter';
import {
  getAssignmentStatus,
  ASSIGNMENT_STATUS_OPTIONS,
} from '../../components/Admin/AssessmenTest/status';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const TableWrapper = styled.div<{ enableVirtualization?: boolean }>`
  .table-scroll-wrapper {
    max-height: 45vh;
    ${({ enableVirtualization }) =>
      enableVirtualization &&
      css`
        height: 45vh;
      `}
  }
  .assessment-tests-table {
    th,
    td {
      &:last-child {
        border-right: none !important;
      }
      &:first-child {
        border-left: none !important;
      }
    }

    tbody > tr:last-child > td {
      border-bottom: none;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }

    th {
      border-radius: 0 !important;
    }
  }
`;

const NavbarWithBreadcrumbs = ({
  name,
  id,
}: {
  name?: string;
  id?: string;
}) => (
  <Navbar
    breadCrumbs={[
      {
        label: 'Home',
        url: ROUTES.HOME.getPath(),
      },
      {
        label: 'Teams',
        url: ROUTES.ADMIN.COMPANIES.getPath(),
      },
      ...(name && id
        ? [
            {
              label: name,
              url: ROUTES.ADMIN.COMPANY.getPath({ companyId: id }),
            },
          ]
        : []),
      { label: 'Assessment Results', url: null },
    ]}
  />
);

const Content = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 32px;
  margin-bottom: 72px;
  gap: 32px;
`;

// Apply confidence-based styling when result exists
const getOpacity = (confidence: number | null) => {
  if (!confidence) {
    return 0.5;
  }
  if (confidence >= 0.8) {
    return 1;
  } else if (confidence >= 0.6) {
    return 0.7;
  } else {
    return 0.6;
  }
};

export default function AssessmentTestsResults() {
  const { id } = useParams<{ id: string }>();
  const {
    data: companyData,
    isLoading: isLoadingCompany,
    error: errorCompany,
    refetch: refetchCompany,
  } = useGetCompany(parseInt(id));
  const {
    data: assessmentTests,
    isLoading: isLoadingAssessmentTests,
    error: errorAssessmentTests,
    refetch: refetchAssessmentTests,
  } = useGetAssessmentTests(parseInt(id));

  const byCohortGroups = React.useMemo(() => {
    return assessmentTests?.cohorts.reduce((acc, cohort) => {
      const cohortId = cohort.id;
      const assignments =
        assessmentTests?.testAssignments.filter(
          (test) => test.cohort.id === cohortId
        ) ?? [];
      if (!acc[cohortId]) {
        acc[cohortId] = [];
      }
      acc[cohortId].push(...assignments);
      return acc;
    }, {} as Record<number, TestAssignment[]>);
  }, [assessmentTests]);

  const sortedCohortGroups = React.useMemo(() => {
    if (!byCohortGroups) return [];
    return Object.entries(byCohortGroups).sort(([id], [id2]) => {
      const aCohortStartedAt = assessmentTests?.cohorts.find(
        (cohort) => cohort.id === parseInt(id)
      )?.createdAt;
      const bCohortStartedAt = assessmentTests?.cohorts.find(
        (cohort) => cohort.id === parseInt(id2)
      )?.createdAt;
      return dayjs.utc(aCohortStartedAt).isBefore(bCohortStartedAt) ? 1 : -1;
    });
  }, [byCohortGroups, assessmentTests]);

  return (
    <PageWithAsyncData
      isLoading={isLoadingCompany || isLoadingAssessmentTests}
      error={errorCompany || errorAssessmentTests}
      retry={() => {
        refetchCompany();
        refetchAssessmentTests();
      }}
      noScroll
    >
      <NavbarWithBreadcrumbs name={companyData?.name} id={id} />
      <CompanyPageWrapper
        currentPage={COMPANY_PAGES.ASSESSMENT_TEST_RESULTS}
        shouldAddEmptyDiv={true}
      >
        <H1>Assessment Results</H1>
        <Content style={{ marginBottom: 0 }}>
          {byCohortGroups &&
            sortedCohortGroups.map(([cohortId, tests], index) => (
              <CohortSection
                isFirstCohort={index === 0}
                testAssignments={tests}
                cohortName={
                  assessmentTests?.cohorts.find(
                    (cohort) => cohort.id === parseInt(cohortId)
                  )?.name ?? ''
                }
              />
            ))}
        </Content>
      </CompanyPageWrapper>
    </PageWithAsyncData>
  );
}

const columnHelper = createColumnHelper<TestAssignment>();

const createColumns = ({
  continuationIds,
}: {
  continuationIds: Set<number>;
}) => [
  columnHelper.display({
    id: 'learner',
    header: 'Name',
    cell: ({ row }) => {
      const isContinuation = continuationIds.has(
        row.original?.assessmentTestSessionId
      );
      return (
        <>
          <div
            className={`learner-${row.original?.assessmentTestSessionId}`}
            style={{
              width: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              opacity: isContinuation ? 0.6 : 1,
            }}
          >
            {isContinuation
              ? `↳ ${row.original?.user?.name ?? '-'}`
              : row.original?.user?.name ?? '-'}
          </div>
          <SmallPortalTooltip
            anchorSelect={`.learner-${row.original?.assessmentTestSessionId}`}
          >
            {row.original?.user?.email}
          </SmallPortalTooltip>
        </>
      );
    },
    size: 200,
    meta: {
      styles: {
        minWidth: '160px',
        maxWidth: '160px',
      },
    },
  }),
  columnHelper.display({
    id: 'status',
    header: 'Status',
    cell: StatusCell,
    size: 120,
    meta: {
      styles: {
        minWidth: '115px',
        maxWidth: '115px',
      },
    },
  }),
  columnHelper.display({
    id: 'score',
    header: 'Score',
    cell: ({ row }) => {
      const results = row.original?.results;
      const isReviewed = !!row.original?.reviewedAt;
      return (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <OverallScorePill
            result={isReviewed ? results : null}
            testSessionId={row.original?.assessmentTestSessionId.toString()}
          />
        </div>
      );
    },
    size: 100,
    meta: {
      styles: {
        minWidth: '100px',
        maxWidth: '100px',
        textAlign: 'center',
      },
    },
  }),
  columnHelper.display({
    id: 'skills',
    header: 'Scores',
    cell: ({ row }) => {
      const dimensions = row.original?.dimensions;
      const results = row.original?.results;
      const isReviewed = !!row.original?.reviewedAt;
      return (
        <SkillsGrid cols={dimensions.length}>
          {dimensions.map((dim) => {
            const dimensionResult = isReviewed
              ? results?.skillScores.find((score) => score.skill === dim.id) ??
                null
              : null;

            return (
              <ScorePill
                key={dim.id}
                result={dimensionResult}
                dimension={dim}
                testSessionId={row.original?.assessmentTestSessionId.toString()}
              />
            );
          })}
        </SkillsGrid>
      );
    },
    size: 550,
    meta: {
      styles: {
        minWidth: '550px',
        maxWidth: '550px',
        textAlign: 'right',
      },
    },
  }),
  columnHelper.accessor('timeSpent', {
    header: 'Time',
    cell: (props) => {
      const timeSpent = props.getValue() ?? 0;

      if (!timeSpent) {
        return '-';
      }

      return dayjs
        .utc()
        .startOf('day')
        .second(timeSpent)
        .format(timeSpent > 3600 ? 'HH:mm:ss' : 'mm:ss');
    },

    meta: {
      styles: {
        minWidth: '100px',
        maxWidth: '100px',
        textAlign: 'right',
      },
    },
  }),
  columnHelper.accessor('invitedAt', {
    header: 'Added At',
    cell: (props) => (
      <DateCellContent date={props.getValue()} label="Invited At" />
    ),
    meta: {
      styles: {
        minWidth: '180px',
      },
    },
  }),
  columnHelper.accessor('startedAt', {
    header: 'Started At',
    cell: (props) => (
      <DateCellContent date={props.getValue()} label="Started At" />
    ),
    meta: {
      styles: {
        minWidth: '180px',
      },
    },
  }),

  columnHelper.accessor('finishedAt', {
    header: 'Finished At',
    cell: (props) => (
      <DateCellContent date={props.getValue()} label="Finished At" />
    ),
    meta: {
      styles: {
        minWidth: '180px',
      },
    },
  }),
  columnHelper.accessor('reviewedAt', {
    header: 'Reviewed At',
    cell: (props) => (
      <DateCellContent date={props.getValue()} label="Reviewed At" />
    ),
    meta: {
      styles: {
        minWidth: '180px',
      },
    },
  }),
  columnHelper.display({
    id: 'name',
    header: 'Test',
    cell: ({ row }) => {
      return <div>{row.original?.name ?? '-'}</div>;
    },
    size: 200,
    enableSorting: true,
    meta: {
      styles: {
        minWidth: '220px',
        maxWidth: '220px',
      },
    },
  }),
];

const SkillsGrid = styled.div<{ cols: number }>`
  display: grid;
  grid-template-columns: repeat(${({ cols }) => cols}, 1fr);
  justify-content: end;
  align-items: center;
  gap: 8px;
`;

const OverallScorePill = ({
  result,
  testSessionId,
}: {
  result: MinimalAssessmentTestResult | null;
  testSessionId: string;
}) => {
  const uniqueId = `overall-score-pill-${testSessionId}`;
  const theme = useTheme();

  if (!result) {
    return (
      <TopicPill
        backgroundColor={theme.colors.bgDefault}
        style={{
          opacity: 0.5,
          justifyContent: 'center',
        }}
      >
        <TopicText>-</TopicText>
      </TopicPill>
    );
  }

  const confidence = result.overallSummary.confidence;
  const score = result.overallSummary.readinessScore;

  return (
    <>
      <TopicPill
        data-tooltip-id={uniqueId}
        backgroundColor={theme.colors.bgDefault}
        style={{
          opacity: getOpacity(result?.overallSummary?.confidence ?? null),
          justifyContent: 'center',
          minWidth: '60px',
        }}
      >
        <TopicText>{score}</TopicText>
      </TopicPill>
      <SmallPortalTooltip anchorSelect={`[data-tooltip-id="${uniqueId}"]`}>
        <TooltipContent>
          <TooltipTitle>Overall Score</TooltipTitle>
          <TooltipDetail>
            Score: <TooltipDetailHighlight>{score}</TooltipDetailHighlight>
          </TooltipDetail>
          <TooltipDetail>
            Confidence:{' '}
            <TooltipDetailHighlight>
              {Math.round(confidence * 100)}%
            </TooltipDetailHighlight>
          </TooltipDetail>
          <TooltipDetail>
            Level:{' '}
            <TooltipDetailHighlight style={{ textTransform: 'capitalize' }}>
              {result.overallSummary.readinessLevel}
            </TooltipDetailHighlight>
          </TooltipDetail>
        </TooltipContent>
      </SmallPortalTooltip>
    </>
  );
};

const ScorePill = ({
  result,
  testSessionId,
  dimension,
}: {
  result: null | MinimalAssessmentTestResult['skillScores'][number];
  dimension: {
    label: string;
    name: string;
    id: string;
  };
  testSessionId: string;
}) => {
  const uniqueId = `score-pill-${dimension.id}-${testSessionId}`;
  const theme = useTheme();

  return (
    <>
      <TopicPill
        data-tooltip-id={uniqueId}
        backgroundColor={theme.colors.bgDefault}
        style={{
          width: '100%',
          justifyContent: 'center',
          opacity: getOpacity(result?.confidence ?? null),
        }}
      >
        <TopicText>
          <span style={{ color: theme.colors.textRegular }}>
            {dimension.label}
          </span>{' '}
          {result?.score ?? '-'}
        </TopicText>
      </TopicPill>
      <SmallPortalTooltip anchorSelect={`[data-tooltip-id="${uniqueId}"]`}>
        <TooltipContent>
          <TooltipTitle>{dimension.name}</TooltipTitle>
          {result ? (
            <>
              <TooltipDetail>
                Score:{' '}
                <TooltipDetailHighlight>{result.score}</TooltipDetailHighlight>
              </TooltipDetail>
              <TooltipDetail>
                Confidence:{' '}
                <TooltipDetailHighlight>
                  {Math.round(result.confidence * 100)}%
                </TooltipDetailHighlight>
              </TooltipDetail>
              <TooltipDetail>
                Level:{' '}
                <TooltipDetailHighlight style={{ textTransform: 'capitalize' }}>
                  {result.comparativeLevel}
                </TooltipDetailHighlight>
              </TooltipDetail>
            </>
          ) : (
            <TooltipDetail>Results not available yet</TooltipDetail>
          )}
        </TooltipContent>
      </SmallPortalTooltip>
    </>
  );
};

const CohortSection = ({
  testAssignments,
  isFirstCohort,
  cohortName,
}: {
  testAssignments: TestAssignment[];
  isFirstCohort: boolean;
  cohortName: string;
}) => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const [isExpanded, setIsExpanded] = React.useState(isFirstCohort);

  const [nameFilter, setNameFilter] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<string[]>([]);
  const [timeRange, setTimeRange] = React.useState<{
    min: number | null;
    max: number | null;
  }>({ min: null, max: null });
  const [groupByLearner, setGroupByLearner] = React.useState(false);

  const onRowClick = React.useCallback(
    (row: TestAssignment) => {
      history.push(
        ROUTES.ADMIN.ASSESSMENT_TEST_RESULT.getPath({
          companyId: id,
          assessmentTestSessionId: row.assessmentTestSessionId.toString(),
        })
      );
    },
    [history, id]
  );

  // Filter the row data before handing it to the table. CompactTable has no
  // filtered-row model, so filtering/grouping happens up front.
  const filteredAssignments = React.useMemo(() => {
    const query = nameFilter.trim().toLowerCase();
    return testAssignments.filter((assignment) => {
      if (query) {
        const name = assignment.user?.name?.toLowerCase() ?? '';
        const email = assignment.user?.email?.toLowerCase() ?? '';
        if (!name.includes(query) && !email.includes(query)) {
          return false;
        }
      }

      if (statusFilter.length > 0) {
        if (!statusFilter.includes(getAssignmentStatus(assignment))) {
          return false;
        }
      }

      if (timeRange.min !== null || timeRange.max !== null) {
        const minutes = (assignment.timeSpent ?? 0) / 60;
        if (timeRange.min !== null && minutes < timeRange.min) return false;
        if (timeRange.max !== null && minutes > timeRange.max) return false;
      }

      return true;
    });
  }, [testAssignments, nameFilter, statusFilter, timeRange]);

  // When grouping is on, cluster sessions by learner email so re-assessments
  // sit adjacent; otherwise keep the default ordering (table sorts by date).
  const { tableData, continuationIds } = React.useMemo(() => {
    if (!groupByLearner) {
      return {
        tableData: filteredAssignments,
        continuationIds: new Set<number>(),
      };
    }

    const byEmail = new Map<string, TestAssignment[]>();
    filteredAssignments.forEach((assignment) => {
      const key = assignment.user?.email ?? `__${assignment.user?.id}`;
      const group = byEmail.get(key) ?? [];
      group.push(assignment);
      byEmail.set(key, group);
    });

    const ordered: TestAssignment[] = [];
    const continuations = new Set<number>();
    Array.from(byEmail.values())
      .sort((a, b) => {
        // Order groups by their most recent activity, newest first.
        const aDate = a[0]?.startedAt ?? a[0]?.invitedAt;
        const bDate = b[0]?.startedAt ?? b[0]?.invitedAt;
        return dayjs.utc(bDate).valueOf() - dayjs.utc(aDate).valueOf();
      })
      .forEach((group) => {
        const sortedGroup = [...group].sort((a, b) => {
          const aDate = a.startedAt ?? a.invitedAt;
          const bDate = b.startedAt ?? b.invitedAt;
          return dayjs.utc(bDate).valueOf() - dayjs.utc(aDate).valueOf();
        });
        sortedGroup.forEach((assignment, index) => {
          ordered.push(assignment);
          if (index > 0) {
            continuations.add(assignment.assessmentTestSessionId);
          }
        });
      });

    return { tableData: ordered, continuationIds: continuations };
  }, [filteredAssignments, groupByLearner]);

  const columns = React.useMemo(
    () => createColumns({ continuationIds }),
    [continuationIds]
  );

  const hasAnyTesters = testAssignments.length > 0;
  const hasVisibleRows = tableData.length > 0;
  const isFiltering =
    nameFilter.trim().length > 0 ||
    statusFilter.length > 0 ||
    timeRange.min !== null ||
    timeRange.max !== null;

  return (
    <CollapsibleSection
      headerLeft={
        <CollapsibleSectionHeader>{cohortName} </CollapsibleSectionHeader>
      }
      headerRight={<CollapsibleSectionLabel>Cohort</CollapsibleSectionLabel>}
      expanded={isExpanded}
      onToggle={() => {
        setIsExpanded(!isExpanded);
      }}
    >
      {hasAnyTesters && <CohortStats testAssignments={testAssignments} />}
      {hasAnyTesters && (
        <FilterBar>
          <TextFilterPill
            label="Name"
            placeholder="Filter by name or email..."
            value={nameFilter}
            onChange={setNameFilter}
          />
          <MultiSelectFilterPill
            label="Status"
            options={ASSIGNMENT_STATUS_OPTIONS}
            selected={statusFilter}
            onChange={setStatusFilter}
          />
          <RangeFilterPill
            label="Time"
            unit="m"
            min={timeRange.min}
            max={timeRange.max}
            onChange={setTimeRange}
          />
          <Toggle
            label="Group by learner"
            checked={groupByLearner}
            onChange={setGroupByLearner}
          />
        </FilterBar>
      )}
      <TableWrapper enableVirtualization={tableData.length > 20}>
        {!hasAnyTesters ? (
          <>
            <EmptyState
              title="No testers invited yet"
              detail="Invite people to this cohort to get started."
            />
            <TableFooter count={0} total={0} />
          </>
        ) : hasVisibleRows ? (
          <Table
            // Remount when grouping toggles so the table re-applies the correct
            // initial sorting (grouped order must not be overridden by a sticky
            // column sort from the ungrouped view).
            key={groupByLearner ? 'grouped' : 'ungrouped'}
            data={tableData}
            initialPinning={{
              left: ['learner'],
            }}
            initialSorting={
              groupByLearner ? [] : [{ id: 'startedAt', desc: true }]
            }
            getRowId={(row) => row.assessmentTestSessionId.toString()}
            columns={columns}
            className="assessment-tests-table"
            footer={
              <TableFooter
                count={tableData.length}
                total={testAssignments.length}
              />
            }
            getRowProps={(row) => ({
              onClick: () => onRowClick(row.original),
            })}
            shouldPinHeaderRow
            enableVirtualization={tableData.length > 20}
            virtualizationRowHeight={50}
          />
        ) : (
          <>
            <EmptyState
              title={
                isFiltering ? 'No matching testers' : 'No completed assessments yet'
              }
              detail={
                isFiltering
                  ? 'Try adjusting or clearing the filters above.'
                  : 'Results will appear here as testers progress.'
              }
            />
            <TableFooter count={0} total={testAssignments.length} />
          </>
        )}
      </TableWrapper>
    </CollapsibleSection>
  );
};

const EmptyStateWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 16px;
`;

const EmptyStateTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const EmptyState = ({ title, detail }: { title: string; detail: string }) => (
  <EmptyStateWrapper>
    <EmptyStateTitle>{title}</EmptyStateTitle>
    <PSmall style={{ marginTop: 0, paddingBottom: 0 }}>{detail}</PSmall>
  </EmptyStateWrapper>
);

const TableFooter = ({ count, total }: { count: number; total: number }) => {
  const showFilteredCount = count !== total;
  return (
    <CollapsibleSectionFooter>
      <CollapsibleSectionLabel>
        {showFilteredCount
          ? `${count} of ${total} ${total === 1 ? 'Tester' : 'Testers'}`
          : `${total} ${total === 1 ? 'Tester' : 'Testers'}`}
      </CollapsibleSectionLabel>
    </CollapsibleSectionFooter>
  );
};
